
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CreateUserDemographicsFields from "./CreateUserDemographicsFields";
import CreateUserLocationFields from "./CreateUserLocationFields";
import { FormData } from "./types";

interface Country {
  code: string;
  name: string;
}

interface CreateUserDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  formData: FormData;
  setFormData: (data: FormData) => void;
  onCreateUser: () => void;
  countries: Country[];
}

const CreateUserDialog = ({
  open,
  setOpen,
  formData,
  setFormData,
  onCreateUser,
  countries,
}: CreateUserDialogProps) => {
  // All state is controlled by formData/setFormData like before

  const handleDemographicsUpdate = (values: any) => {
    setFormData({
      ...formData,
      demographics: values,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="
          max-h-[90vh]
          overflow-y-auto
          p-0
          flex flex-col
          w-full
          max-w-lg
          rounded-lg
          bg-background
          "
        style={{ maxHeight: '90vh' }}
      >
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2 bg-background z-10 sticky top-0">
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Fill out the form to provision a new user profile. (NB: User must still register credentials via auth.)
          </DialogDescription>
        </DialogHeader>
        {/* Content area */}
        <div
          className="grow px-6 py-4 overflow-y-auto space-y-6"
          style={{ minHeight: 0, maxHeight: '60vh' }}
        >
          <div className="space-y-2">
            <Label className="mb-1">Name</Label>
            <Input
              value={formData.name || ""}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          
          {/* Email field */}
          <div className="space-y-2">
            <Label className="mb-1">Email (Username)</Label>
            <Input
              type="email"
              value={formData.email || ""}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="user@example.com"
            />
          </div>
          
          {/* Password field */}
          <div className="space-y-2">
            <Label className="mb-1">Temporary Password</Label>
            <Input
              type="text"
              value={formData.password || ""}
              onChange={e => setFormData({ ...formData, password: e.target.value })}
              required
              placeholder="Temporary password"
            />
            <p className="text-xs text-muted-foreground">
              This password will be shown to you after creation for sharing with the user.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label className="mb-1">Role</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors"
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value as FormData["role"]})}
            >
              <option value="public">Public</option>
              <option value="pollster">Pollster</option>
              <option value="media">Media</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          
          {/* Location fields - ensures passing all required props */}
          <CreateUserLocationFields
            countries={countries}
            countryCode={formData.country_code}
            setCountryCode={(code) => setFormData({ ...formData, country_code: code, constituency_code: "" })}
            constituencyCode={formData.constituency_code}
            setConstituencyCode={(code) => setFormData({ ...formData, constituency_code: code })}
            phoneNumber={formData.phone_number || ""}
            setPhoneNumber={(number) => setFormData({ ...formData, phone_number: number })}
          />
          
          {/* Padding top and border above demographic fields */}
          <div className="pt-4 border-t border-gray-200">
            <CreateUserDemographicsFields
              demographics={formData.demographics}
              setDemographics={handleDemographicsUpdate}
            />
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 px-6 pb-6 pt-4 bg-background z-10 sticky bottom-0 space-x-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onCreateUser}
            className="shadow-md border border-transparent"
          >
            Create User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserDialog;
